import { action, computed, decorate, observable, runInAction } from 'mobx';

import { fetchExistingEmbeddedIntegrations } from '../api/embedded-integrations';
import { integrationStatus } from '../api';
import { generateGUID } from '../utils/guid';

class EmbeddedIntegrationsStore {
  constructor({ config }) {
    this.config = config;

    this.siteId = '';
    this._isWixSite = false;
    this.primaryDomain = '';
    this.embeddedIntegrationsData = new Map();
    this.embeddedIntegrationsReady = false;
  }

  generateEmbeddedIntegration = (integrationConfig) => {
    const newIntegration = {
      content: {
        type: integrationConfig.type,
      },
      domain: this._isWixSite ? undefined : this.primaryDomain,
      embedType: integrationConfig.type,
      enabled: integrationConfig.enabled || true,
      id: generateGUID(),
      loadOnce: integrationConfig.loadOnce || false,
      name: integrationConfig.name || 'custom',
      pages: null,
      position: integrationConfig.position || 'head',
      siteId: this.siteId,
    };

    if (integrationConfig.checkbox) {
      newIntegration.content[integrationConfig.checkbox.type] = false;
    }
    if (integrationConfig.trackingId) {
      newIntegration.content.trackingId = '';
    }

    return newIntegration;
  };

  initStoreData = async () => {
    try {
      const {
        htmlEmbeds,
        domains,
        primaryDomain,
        siteId,
        isWixSite,
      } = await fetchExistingEmbeddedIntegrations(this.config);
      runInAction(() => {
        this.siteId = siteId;
        this._isWixSite = isWixSite;
        this.primaryDomain = primaryDomain || domains[0];
        htmlEmbeds.forEach((embed) => {
          const {
            content: { type },
          } = embed;
          this.embeddedIntegrationsData.set(type, embed);
        });
        this.embeddedIntegrationsReady = true;
      });
    } catch (ex) {
      return Promise.reject(
        `Error initializing embedded integrations data. ${ex}`,
      );
    }
  };

  setEmbeddedIntegrationProp = (type, property, value) => {
    if (this.embeddedIntegrationsData.has(type)) {
      const integrationData = this.embeddedIntegrationsData.get(type);
      integrationData[property] = value;
      this.embeddedIntegrationsData.set(type, integrationData);
    } else {
      throw new Error(
        'Embedded integrations error: trying to update property for an undefined integration',
      );
    }
  };

  setEmbeddedIntegration = (type, integrationData) =>
    this.embeddedIntegrationsData.set(type, integrationData);

  deleteEmbeddedIntegration = (type) =>
    this.embeddedIntegrationsData.delete(type);

  getSiteId = () => this.siteId;

  getEmbeddedIntegration = (type) => this.embeddedIntegrationsData.get(type);

  getEmbeddedIntegrationExist = () => !!this.embeddedIntegrationsData.size;

  getIntegrationStatus = (type) => {
    if (this.embeddedIntegrationsData.has(type)) {
      const { enabled } = this.embeddedIntegrationsData.get(type);
      return enabled
        ? integrationStatus.CONNECTED
        : integrationStatus.DISCONNECTED;
    } else {
      return integrationStatus.NEW;
    }
  };

  get isDomainConnected() {
    return !!this.primaryDomain;
  }

  get isWixSite() {
    return this._isWixSite;
  }

  get areEmbeddedIntegrationsReady() {
    return this.embeddedIntegrationsReady;
  }
}

export default decorate(EmbeddedIntegrationsStore, {
  siteId: observable,
  _isWixSite: observable,
  primaryDomain: observable,
  embeddedIntegrationsData: observable,
  embeddedIntegrationsReady: observable,
  isDomainConnected: computed,
  isWixSite: computed,
  areEmbeddedIntegrationsReady: computed,
  setEmbeddedIntegrationProp: action,
  setEmbeddedIntegration: action,
  deleteEmbeddedIntegration: action,
});
